import { useEffect } from 'react';

import { daysFromNow, parse } from '@eneco/common/date';
import RichText from '@eneco/components/RichText/RichText';
import useDC from '@eneco/dc/useDC';
import { StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { Checkbox, InputText, NotificationBox } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { DeliveryDateStepRendering } from '@sitecore/types/DeliveryDateStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { CONTRACT_CONSIDERATION_PERIOD_IN_DAYS, getMinUrgentContractStartDateInDays } from '../../../shared/helpers';

interface FormFields {
  deliveryDate: string;
  isUrgent: boolean;
}

export const DeliveryDateStep = () => {
  const { DeliveryDateStep: { fields: textualData } = {} } = usePlaceholderContent<{
    DeliveryDateStep: DeliveryDateStepRendering;
  }>();

  const { label } = useDC();

  const { format, date } = useFormatter();
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const sendToFlowMachine = useFlowActorRef().send;
  const {
    context: { deliveryDate, isUrgent },
  } = flowState;

  const defaultDate = date.short(daysFromNow(getMinUrgentContractStartDateInDays(label)));

  const formSchema = yup.object({
    deliveryDate: yup
      .date()
      .typeError(textualData?.deliveryDate?.deliveryDateFormField?.value?.validationMessage ?? '')
      .nullable()
      .min(
        daysFromNow(getMinUrgentContractStartDateInDays(label)),
        textualData?.deliveryDate?.deliveryDateFormField?.value?.validationMinValueMessage,
      )
      .max(daysFromNow(CONTRACT_CONSIDERATION_PERIOD_IN_DAYS), '')
      .required(textualData?.deliveryDate?.deliveryDateFormField?.value?.requiredMessage)
      .transform((_, originalValue) => {
        return parse(originalValue);
      }),
    isUrgent: yup.boolean().oneOf([true]),
  });

  const {
    formState: { errors, isSubmitSuccessful },
    watch,
    getValues,
    handleSubmit,
    register,
    setValue,
  } = useForm<FormFields>({
    mode: 'onTouched',
    resolver: typedYupResolver<FormFields>(formSchema),
    defaultValues: {
      deliveryDate: deliveryDate ?? defaultDate,
      isUrgent,
    },
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const { deliveryDate, isUrgent } = getValues();
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: { deliveryDate, isUrgent },
      });
    }
  }, [date, getValues, isSubmitSuccessful, sendToFlowMachine]);

  const deliveryDateField = watch('deliveryDate');
  const deliveryDateText = !errors?.deliveryDate ? deliveryDateField : '';

  const resetIsUrgent = () => {
    if (getValues('isUrgent')) {
      setValue('isUrgent', false);
    }
  };

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <StepHeader textualData={textualData} />
      <InputText
        {...register('deliveryDate', {
          onChange: () => resetIsUrgent(),
        })}
        hint={textualData?.deliveryDate?.deliveryDateFormField?.value?.hint}
        label={textualData?.deliveryDate?.deliveryDateFormField?.value?.label ?? ''}
        error={errors.deliveryDate?.message}
      />

      {!errors?.deliveryDate ? (
        <Checkbox
          {...register('isUrgent')}
          error={errors?.isUrgent && textualData?.deliveryDate?.isUrgentFormField?.value?.requiredMessage}
          label={
            textualData?.deliveryDate?.isUrgentFormField?.value?.label
              ? format(textualData?.deliveryDate?.isUrgentFormField?.value?.label, { deliveryDate: deliveryDateText })
              : ''
          }
        />
      ) : null}

      {errors?.deliveryDate?.type === 'max' ? (
        <NotificationBox
          variant={textualData?.deliveryDate?.notUrgentNotification?.value?.variant}
          isAlert={false}
          title={textualData?.deliveryDate?.notUrgentNotification?.value?.title}
          text={<RichText html={textualData?.deliveryDate?.notUrgentNotification?.value?.content} />}
        />
      ) : (
        <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
      )}
    </Layout.Content>
  );
};
